import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactTimeout from 'react-timeout';
import classNames from 'classnames';
import Hls from 'hls.js';
import _ from 'lodash';
import handleShouldComponentUpdate from 'helpers/Lifecycle';
import Style from 'app/themes/VideoHero.module.scss';

const TRANSITION_TIME_IN = 500;
const TRANSITION_TIME_OUT = 500;
const POSTER_IMAGE_ALT = 'Video Poster';
const { backgroundVideoLoops } = window.ExternalConfig;

/**
 * VideoHero Component
 * @param {Object} theme        - Theme override sent through CSS Themr
 * @param {String} externalId   - key for the VideoPlayerItem
 * @returns {Component}         - React Component
 */
class VideoHero extends Component {
  constructor() {
    super();
    this.state = {
      videoElement: null,
      hlsObjects: [this.createNewHls()],
      initialized: false,
      loaded: false,
      transition: false,
      videoImage: null,
      videoTagError: false,
      videoPlayCount: 1,
    };
    this.videoTag = React.createRef();
    this.videoTagListener = this.videoTagListener.bind(this);
  }

  componentDidMount() {
    const { video, load, onMount, play, transitionImage } = this.props;
    if (onMount) onMount();
    if (load) {
      if (video) {
        this.loadVideo({ play, video });
      } else if (transitionImage) {
        const videoElement = this.getVideoElement();
        this.setState({ videoElement, transition: true }, this.transitionImageStart);
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { play, video, image, transitionImage, setTimeout } = this.props;
    const {
      play: nextPlay,
      video: nextVideo,
      image: nextImage,
      transitionImage: nextTransitionImage,
    } = nextProps;
    const { videoElement, loaded, initialized } = this.state;

    // Load video right away if it's the first one
    if (!initialized && nextVideo) {
      this.loadVideo({ play: nextPlay, video: nextVideo });
      this.setState({ initialized: true });
      return;
    }

    // Start hero transition if the source is changing
    if (video !== nextVideo || image !== nextImage || transitionImage !== nextTransitionImage) {
      this.setState({ transition: true });
      if (nextVideo) {
        // If transitioning to a video, load video before completing transition
        setTimeout(this.transitionStart.bind(this), TRANSITION_TIME_IN);
      } else {
        // Otherwise, unload the video and start the end transition
        setTimeout(this.transitionImageStart.bind(this), TRANSITION_TIME_IN);
      }
    } else if (play !== nextPlay && loaded) {
      if (!nextPlay) {
        videoElement.pause();
        this.handleCallback('paused');
      } else {
        this.playVideoSafely(videoElement);
        this.handleCallback('playing');
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const options = {
      props: this.props,
      state: this.state,
      nextProps,
      nextState,
    };
    return handleShouldComponentUpdate(options);
  }

  componentWillUnmount() {
    const { onUnmount } = this.props;
    if (onUnmount) onUnmount();
  }

  getHlsObject() {
    const { hlsObjects } = this.state;
    return _.last(hlsObjects);
  }

  getVideoElement() {
    let { videoElement } = this.state;
    videoElement = this.videoTag.current;
    videoElement.addEventListener('error', this.videoTagListener, false);
    videoElement.addEventListener('loadeddata', this.videoTagListener, false);
    if (backgroundVideoLoops !== 0)
      videoElement.addEventListener('seeked', this.videoTagListener, false);
    return videoElement;
  }

  loadVideo({ video }) {
    if (!video) return;

    const hlsObject = this.getHlsObject();
    const { videoElement: oldVideoElement } = this.state;
    const videoElement = this.getVideoElement();

    videoElement.setAttribute('src', video);
    hlsObject.loadSource(video);
    hlsObject.attachMedia(videoElement);

    if (!oldVideoElement) this.setState({ videoElement });
  }

  listenerAttachedVideo() {
    const { setTimeout } = this.props;
    setTimeout(this.transitionEnd.bind(this), TRANSITION_TIME_OUT);
  }

  handleParsedVideo() {
    const { play, timer: currentTimer, setTimeout } = this.props;
    let timer = parseInt(currentTimer, 10);
    timer = Number.isNaN(timer) ? 0 : timer * 1000;

    if (play) {
      setTimeout(() => {
        const { play: playNow } = this.props;
        if (playNow) {
          this.playVideoSafely();
          this.handleCallback('playing');
        }
      }, timer);
    } else {
      this.handleCallback('loaded');
    }
    this.setState({ loaded: true });
  }

  playVideoSafely(videoElement) {
    const { videoElement: currentVideoElement } = this.state;
    const video = videoElement || currentVideoElement;
    const playPromise = video.play();
    if (playPromise) {
      playPromise.catch(() => {
        /* prevents the video from throwing an error. */
      });
    }
  }

  transitionImageStart() {
    const { video, transitionImage } = this.props;
    const { hlsObjects, setTimeout } = this.state;
    const hlsObject = this.getHlsObject();
    hlsObject.destroy();
    const videoElement = this.getVideoElement();
    const newVideoImage = hlsObject && video ? null : transitionImage;
    const newHlsObject = this.createNewHls();
    videoElement.src = '';
    this.loadPoster({ poster: newVideoImage });
    videoElement.load();

    const newHlsObjects = _.concat(hlsObjects, newHlsObject);
    this.setState({ videoImage: newVideoImage, videoElement, hlsObjects: newHlsObjects });
    setTimeout(this.transitionEnd.bind(this), TRANSITION_TIME_OUT);
  }

  transitionEnd() {
    this.setState({ transition: false });
  }

  transitionStart() {
    const { video } = this.props;
    const { videoElement, hlsObjects } = this.state;
    const hlsObject = this.getHlsObject();
    hlsObject.destroy();
    const newHlsObject = this.createNewHls();
    if (!newHlsObject) return;
    videoElement.setAttribute('src', video);
    videoElement.setAttribute('style', null);
    newHlsObject.loadSource(video);
    newHlsObject.attachMedia(videoElement);

    const newHlsObjects = _.concat(hlsObjects, newHlsObject);
    this.setState({ videoImage: null, videoElement, hlsObjects: newHlsObjects });
  }

  createNewHls() {
    const config = {
      maxBufferHole: 0,
      maxLoadingDelay: 1000,
    };
    const newHlsObject = new Hls(config);
    const listenerParsedVideo = e => this.handleParsedVideo(e);
    const listenerAttachedVideo = e => this.listenerAttachedVideo(e);
    newHlsObject.on(Hls.Events.MANIFEST_PARSED, listenerParsedVideo);
    newHlsObject.on(Hls.Events.MEDIA_ATTACHED, listenerAttachedVideo);
    return newHlsObject;
  }

  videoTagListener = event => {
    if (!event) return;

    let { videoPlayCount } = this.state;
    const { videoElement } = this.state;
    switch (event.type) {
      case 'loadeddata':
        this.setState({ videoTagError: false, videoPlayCount: 1 }, () => {
          this.handleParsedVideo(event);
          this.listenerAttachedVideo(event);
        });
        break;
      case 'error':
        this.setState({ videoTagError: true, videoPlayCount: 1 });
        break;
      case 'seeked':
        if (videoPlayCount > backgroundVideoLoops) {
          this.setState({ videoPlayCount: 1 });
          videoElement.pause();
          this.handleCallback('paused');
        }
        videoPlayCount += 1;
        this.setState({ videoPlayCount });
        break;
      default:
      // do nothing
    }
  };

  loadPoster({ poster }) {
    if (!poster) return;
    const { videoElement: oldVideoElement } = this.state;
    const videoElement = this.getVideoElement();
    if (!oldVideoElement) this.setState({ videoElement });
  }

  handleCallback(...args) {
    const { callback } = this.props;
    const cb = callback || null;
    if (cb && typeof cb === 'function') {
      cb.apply(cb, args);
    }
  }

  renderTransitionImage() {
    const { transitionImage } = this.props;
    if (!transitionImage) return null;

    return <img src={transitionImage} className={Style.transitionPoster} alt={POSTER_IMAGE_ALT} />;
  }

  renderPoster() {
    const {
      video,
      image,
      transitionImage,
      renderImage,
      addShadowOverlay,
      anonymousView,
    } = this.props;
    const { videoImage } = this.state;
    if (!image) return null;

    const isVideoPoster = video || transitionImage;
    const posterSrc = videoImage || image;
    const posterClass = classNames(
      Style.poster,
      addShadowOverlay ? Style.posterPadding : null,
      anonymousView ? Style.anonymousView : null,
    );

    if (!isVideoPoster) {
      return (
        <div className={posterClass}>
          {renderImage({ src: posterSrc, alt: POSTER_IMAGE_ALT, lazy: false })}
        </div>
      );
    }

    return renderImage({
      className: Style.videoPoster,
      src: posterSrc,
      alt: POSTER_IMAGE_ALT,
      lazy: false,
    });
  }

  render() {
    const { id, video, play, loop, fadeImageToVideo, className, addShadowOverlay } = this.props;
    const { loaded, transition, videoTagError } = this.state;

    const containerClassName = classNames(
      className,
      Style.container,
      addShadowOverlay ? Style.shadowOverlay : null,
    );
    const wrapperPosterVideo = video ? Style.wrapperVideo : Style.wrapperPoster;
    const wrapperBlack = play || addShadowOverlay ? null : Style.wrapperBlack;

    const wrapperClassName = classNames(
      Style.wrapper,
      wrapperBlack,
      loaded ? Style.videoLoaded : null,
      addShadowOverlay ? null : wrapperPosterVideo,
      fadeImageToVideo ? Style.fadeImageToVideo : null,
      transition || videoTagError ? Style.transition : null,
    );

    const videoProps = {
      id,
      muted: 'muted',
      playsInline: 'playsInline',
    };
    if (video && loop) videoProps.loop = 'loop';
    if (video && play) videoProps.autoPlay = 'autoPlay';

    return (
      <div className={containerClassName}>
        <div className={wrapperClassName}>
          {this.renderPoster()}
          <video ref={this.videoTag} className={Style.player} {...videoProps} />
          {this.renderTransitionImage()}
        </div>
      </div>
    );
  }
}

VideoHero.defaultProps = {
  anonymousView: false,
  callback: () => {},
  className: null,
  fadeImageToVideo: true,
  id: 'video-hero',
  image: null,
  load: null,
  loop: true,
  onMount: () => {},
  onUnmount: () => {},
  play: null,
  renderImage: () => {},
  timer: null,
  transitionImage: null,
  video: null,
  addShadowOverlay: false,
};

VideoHero.propTypes = {
  anonymousView: PropTypes.bool,
  callback: PropTypes.func,
  className: PropTypes.string,
  fadeImageToVideo: PropTypes.bool,
  id: PropTypes.string,
  image: PropTypes.string,
  load: PropTypes.bool,
  loop: PropTypes.bool,
  onMount: PropTypes.func,
  onUnmount: PropTypes.func,
  play: PropTypes.bool,
  renderImage: PropTypes.func,
  setTimeout: PropTypes.func.isRequired,
  timer: PropTypes.number,
  transitionImage: PropTypes.string,
  video: PropTypes.string,
  addShadowOverlay: PropTypes.bool,
};

export default ReactTimeout(VideoHero);
